import { TableCell, Tooltip } from "@mui/material";
import { IAdditional } from "../TableTemplate/tableTemplate";
import Button from "../../Components/Button";
import {
  IResetSiteUserPasswordFormValues,
  ISiteUsersTableReport,
} from "../../Interfaces/siteUserInterfaces";
import {
  disableSiteUser,
  enableSiteUser,
  resetSiteUserPassword,
} from "../../Requests/siteUserRequests";
import { toast } from "react-toastify";
import PersonIcon from "@mui/icons-material/Person";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";
import Modal from "../../Components/Modal/Modal";
import TextInput from "../../Components/TextInput";
import AddOrEditSiteUserForm from "../../Forms/AddOrEditSiteUser";
import { packetErrorHandler } from "../../Utils/requestUtils";

const SiteUsersTableAdditionalHeader: React.FC = () => {
  return (
    <TableCell>
      <span className="!font-black text-black">عملیات</span>
    </TableCell>
  );
};

const SiteUsersAdditionalCol: React.FC<{
  row: ISiteUsersTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [isResetPasswordLoading, setIsResetPasswordLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [resetPassFormValues, setResetPassFormValues] = useState({
    userid: row.id,
  } as IResetSiteUserPasswordFormValues);

  const enableHandler = () => {
    enableSiteUser(row.id)
      .then((res) => {
        if (res.data) {
          toast.success(res.msg);
          onAction();
        } else toast.error(res.msg);
      })
      .catch(packetErrorHandler);
  };

  const deleteHandler = () => {
    disableSiteUser(row.id)
      .then((res) => {
        if (res.data) {
          toast.success(res.msg);
          setIsDeleteOpen(false);
          onAction();
        } else toast.error(res.msg);
      })
      .catch(packetErrorHandler);
  };

  const resetPasswordModalCloseHandler = () => {
    setIsResetPasswordOpen(false);
    setResetPassFormValues({
      userid: row.id,
    } as IResetSiteUserPasswordFormValues);
  };

  const checkDisabled = () => {
    if (!resetPassFormValues) return true;
    if (!resetPassFormValues.password || !resetPassFormValues.confirmPassword)
      return true;
    if (resetPassFormValues.confirmPassword !== resetPassFormValues.password)
      return true;
    return false;
  };

  function errorHandler(e: any) {
    let error: any = "";

    if (e.msg) error = e.msg;
    if (e?.response?.data?.value) error = e.response.data.value;
    if (error === "") error = "خطا در ارتباط با سرور";
    toast.error(error);
  }

  const resetPassClickHandler = () => {
    setIsResetPasswordLoading(true);
    resetSiteUserPassword(resetPassFormValues)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          resetPasswordModalCloseHandler();
        } else toast.error(resp.msg);
      })
      .catch(errorHandler)
      .finally(() => setIsResetPasswordLoading(false));
  };

  const changeHandler = (name: string, value: any) => {
    setResetPassFormValues({ ...resetPassFormValues, [name]: value });
  };

  const getModalUI = () => {
    return (
      <div className="flex flex-col justify-between gap-2 items-center">
        <TextInput
          label="رمز عبور جدید"
          name="password"
          value={resetPassFormValues.password}
          onChange={changeHandler}
          type="password"
          autoComplete="new-password"
        />
        <TextInput
          label="تایید رمز عبور"
          name="confirmPassword"
          value={resetPassFormValues.confirmPassword}
          onChange={changeHandler}
          type="password"
          autoComplete="new-password"
        />
        <Button
          text="تایید"
          variant="contained"
          onClick={resetPassClickHandler}
          disabled={checkDisabled()}
          loading={isResetPasswordLoading}
        />
      </div>
    );
  };

  return (
    <TableCell>
      {isResetPasswordOpen ? (
        <Modal title="تغییر رمز عبور" onClose={resetPasswordModalCloseHandler} UI={getModalUI()} />
      ) : null}
      {isEditOpen ? (
        <Modal title="ویرایش کاربر سایت"
          UI={
            <AddOrEditSiteUserForm
              userId={row.id}
              onClose={() => {
                setIsEditOpen(false);
                onAction();
              }}
            />
          }
          onClose={() => setIsEditOpen(false)}
          modalBoxClassName="!min-w-[340px]"
        />
      ) : null}
      {isDeleteOpen ? (
        <Modal title="غیرفعال‌کردن کاربر"
          onClose={() => setIsDeleteOpen(false)}
          modalBoxClassName="compact-confirm-modal"
          UI={
            <div
              dir="rtl"
              className="w-full flex flex-col gap-6 items-stretch text-right"
            >
              <div className="flex flex-col gap-2">
                <div className="text-base font-bold text-slate-800">
                  غیرفعال‌کردن کاربر
                </div>
                <div className="text-sm leading-6 text-slate-600">
                  آیا از غیرفعال‌کردن کاربر «{row.userName}» مطمئن هستید؟ کاربر
                  از فهرست کاربران فعال حذف می‌شود، اما اطلاعات او پاک نخواهد
                  شد.
                </div>
              </div>
              <div className="flex flex-row gap-2 w-full">
                <Button
                  text="انصراف"
                  variant="outlined"
                  onClick={() => setIsDeleteOpen(false)}
                />
                <Button
                  text="غیرفعال‌کردن"
                  variant="contained"
                  onClick={deleteHandler}
                />
              </div>
            </div>
          }
        />
      ) : null}
      <div className="flex flex-row gap-2 w-min">
        <Tooltip title="ویرایش" arrow>
          <Button
            className="table-btn"
            onClick={() => setIsEditOpen(true)}
            text={<EditIcon className="!text-md" />}
            variant="contained"
          />
        </Tooltip>
        {row.isActive ? (
          <Tooltip title="حذف (غیرفعال‌سازی)" arrow>
            <Button
              className="table-btn"
              onClick={() => setIsDeleteOpen(true)}
              text={<DeleteIcon className="!text-md" />}
              variant="contained"
            />
          </Tooltip>
        ) : (
          <Tooltip title="فعال کردن" arrow>
            <Button
              className="table-btn"
              onClick={enableHandler}
              text={<PersonIcon className="!text-md" />}
              variant="contained"
            />
          </Tooltip>
        )}
        <Tooltip title="ریست رمز عبور" arrow>
          <Button
            className="table-btn"
            onClick={() => setIsResetPasswordOpen(true)}
            text={<LockResetIcon className="!text-md" />}
            variant="contained"
          />
        </Tooltip>
      </div>
    </TableCell>
  );
};

export const SiteUsersTableAdditional: IAdditional<ISiteUsersTableReport> = {
  getHeader: SiteUsersTableAdditionalHeader,
  getRow: SiteUsersAdditionalCol,
};
